import {
  Alert,
  Center,
  DrawerBody,
  DrawerHeader,
  Flex,
  HStack,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
  Text,
} from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useState, useCallback, useMemo } from 'react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { RegionForm } from './RegionForm';
import { RegionSites } from './RegionSites';
import { LocationError } from './LocationError';
import { RegionsLocationsURLParams } from './types';

export function RegionLocationsDetails() {
  const { providerId, regionId } = useParams<RegionsLocationsURLParams>();
  const [activeTab, setActiveTab] = useState<number>(0);

  const tabs = useMemo(
    () => [
      { value: 'details', label: t`Details` },
      { value: 'sites', label: t`Sites` },
    ],
    [],
  );

  const handleTabChange = useCallback(
    (index: number) => {
      if (typeof tabs[index] !== 'undefined') {
        setActiveTab(index);
      } else {
        setActiveTab(0);
      }
    },
    [activeTab, tabs],
  );

  if (!providerId || !regionId) {
    return <LocationError message={t`An error occurred, providerId and regionId are required.`} />;
  }

  const { data: region, isLoading } = trpcAdmin.providers.locations.getSiteRegion.useQuery(
    {
      providerId,
      id: regionId,
    },
    { enabled: Boolean(!!providerId && !!regionId) },
  );

  if (isLoading || !region) {
    return (
      <Center h="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (!region) {
    return (
      <Alert>
        <Trans>No site found</Trans>
      </Alert>
    );
  }

  return (
    <Flex flexDirection="column" w="100%" h="100%">
      <DrawerHeader
        borderBottomWidth="1px"
        borderBottomColor="border.default"
        p={{ base: 2, md: 4 }}
      >
        <HStack justify="space-between" alignContent="center">
          <HStack align="center" h="full" alignSelf="stretch">
            <Text fontSize="lg" fontWeight="semibold">
              {region.name}
            </Text>
            {region.is_default ? (
              <Tag size="sm" variant="solid" colorScheme="brand">
                {t`Primary`}
              </Tag>
            ) : null}
          </HStack>
        </HStack>
      </DrawerHeader>

      <DrawerBody p="0" border="none" m="0" paddingInline="0 !important">
        <Tabs
          isLazy
          flexDirection="column"
          display="flex"
          w="100%"
          h="100%"
          index={activeTab}
          onChange={handleTabChange}
        >
          <TabList bg="bg.subtle">
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                bg={activeTab === index ? 'bg.surface' : ''}
                _hover={{ bg: 'bg.muted' }}
              >
                {tab.label}
              </Tab>
            ))}
          </TabList>

          <TabPanels width="100%" height="100%" overflow="auto">
            <TabPanel width="100%" height="100%" overflow="scroll" p={0}>
              {region ? <RegionForm region={region} /> : null}
            </TabPanel>
            <TabPanel width="100%" height="100%" overflow="scroll" p={0}>
              {region ? <RegionSites region={region} /> : null}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </DrawerBody>
    </Flex>
  );
}
